import { MdCheck, MdClose } from "react-icons/md";
import Modal from "../Modal/Modal";
import { API_ENDPOINT_UPDATE_PROFILE, ICON_SIZES } from "src/helpers/constants";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import CustomSelect from "../Select/CustomSelect";
import { useMemo, useState } from "react";
import CheckBox from "../CheckBox/CheckBox";
import useCurrentUser from "src/hooks/useCurrentUser";
import useCache from "src/hooks/useCache";
import apiClient from "src/helpers/api-client";
import { objectHasChanges, showNotification } from "src/helpers/utils";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";
import ChangesPendingToSave from "../ChangesPendingToSave/ChangesPendingToSave";
import { EButtonKind } from "../Button/ButtonTypes";
import i18n from "i18next";

interface ICurrentUserPrivacyModal {
  onClose: () => void;
}

export enum EPrivacyVisibility {
  EVERYONE = "EVERYONE",
  CONTACTS = "CONTACTS",
  NONE = "NONE",
}

const WHO_CAN_SEE = [
  {
    value: EPrivacyVisibility.EVERYONE,
    label: i18n.t("privacy_modal.options.everyone"),
  },
  {
    value: EPrivacyVisibility.CONTACTS,
    label: i18n.t("privacy_modal.options.contacts"),
  },
  {
    value: EPrivacyVisibility.NONE,
    label: i18n.t("privacy_modal.options.nobody"),
  },
];

const CurrentUserPrivacyModal: React.FC<ICurrentUserPrivacyModal> = ({
  onClose,
}) => {
  const { t } = useTranslation();
  const [isUpdatingSettings, setIsUpdatingSettings] = useState<boolean>(false);
  const { clearCache } = useCache();
  const { settings } = useCurrentUser();
  const [showConfirmSaveDialog, setShowConfirmSaveDialog] =
    useState<boolean>(false);
  const [picturePermission, setPicturePermission] =
    useState<EPrivacyVisibility>(
      settings?.privacy?.seePicture || EPrivacyVisibility.EVERYONE
    );
  const [introPermission, setIntroPermission] = useState<EPrivacyVisibility>(
    settings?.privacy?.seeIntro || EPrivacyVisibility.EVERYONE
  );
  const [contactInfoPermission, setContactInfoPermission] =
    useState<EPrivacyVisibility>(
      settings?.privacy?.seeContact || EPrivacyVisibility.EVERYONE
    );
  const [publicProfile, setPublicProfile] = useState<boolean>(
    !!settings?.privacy?.allowFindMe
  );

  const updateProfileSettings = async () => {
    setIsUpdatingSettings(true);

    const formData = new FormData();

    formData.append(
      "settings",
      JSON.stringify({
        ...settings,
        privacy: {
          ...(settings?.privacy || {}),
          allowFindMe: publicProfile,
          seePicture: picturePermission,
          seeIntro: introPermission,
          seeContact: contactInfoPermission,
        },
      })
    );

    return apiClient
      .put(API_ENDPOINT_UPDATE_PROFILE, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then(async (res: any) => {
        await clearCache();

        showNotification({
          kind: "success",
          message: "Cambios guardados",
        });
        setShowConfirmSaveDialog(false);
        onClose();
      })
      .catch((e) => {
        showNotification({
          message: "Error guardando los cambios",
          kind: "error",
        });
      })
      .finally(() => {
        setIsUpdatingSettings(false);
      });
  };

  const hasChanges = useMemo(() => {
    return objectHasChanges({
      original: settings?.privacy || {},
      edited: {
        allowFindMe: publicProfile,
        seePicture: picturePermission,
        seeIntro: introPermission,
        seeContact: contactInfoPermission,
      },
    });
  }, [
    publicProfile,
    picturePermission,
    introPermission,
    contactInfoPermission,
    settings?.privacy,
  ]);

  return (
    <>
      <Modal
        onEscape={() => {
          if (!isUpdatingSettings) {
            onClose?.();
          }
        }}
        className="md:!w-[520px] "
      >
        <Modal.Header
          onClose={() => {
            if (!isUpdatingSettings) {
              onClose?.();
            }
          }}
          title={t("privacy_modal.title")}
        />
        <Modal.Body>
          <div
            className={clsx([
              "flex",
              "w-full",
              "gap-4",
              "md:gap-6",
              "flex-col",
            ])}
          >
            <CheckBox
              checked={publicProfile}
              label={t("privacy_modal.allow_find_me")}
              description={t("privacy_modal.allow_find_me_description")}
              onChange={(value: boolean) => setPublicProfile(value)}
            />
            <CustomSelect
              options={WHO_CAN_SEE}
              label={t("privacy_modal.fields.picture")}
              onChange={(e: any) => setPicturePermission(e?.value)}
              value={picturePermission}
              isSearchable={false}
              isClearable={false}
              useValueAsLabel={false}
            />
            <CustomSelect
              options={WHO_CAN_SEE}
              label={t("privacy_modal.fields.intro")}
              value={introPermission}
              onChange={(e: any) => setIntroPermission(e?.value)}
              isSearchable={false}
              isClearable={false}
              useValueAsLabel={false}
            />
            <CustomSelect
              options={WHO_CAN_SEE}
              label={t("privacy_modal.fields.contact")}
              onChange={(e: any) => setContactInfoPermission(e?.value)}
              value={contactInfoPermission}
              isSearchable={false}
              isClearable={false}
              useValueAsLabel={false}
            />
          </div>
        </Modal.Body>
        <Modal.Footer
          leftContent={<ChangesPendingToSave show={hasChanges} />}
          actions={[
            {
              kind: EButtonKind.SAVE,
              icon: <MdCheck size={ICON_SIZES.MD} />,
              onClick: () => setShowConfirmSaveDialog(true),
              text: t("common_buttons.update"),
              isLoading: isUpdatingSettings,
              disabled: isUpdatingSettings || !hasChanges,
            },
            {
              text: t("common_buttons.close"),
              onClick: () => {
                if (!isUpdatingSettings) {
                  onClose?.();
                }
              },
              kind: EButtonKind.NORMAL,
              icon: <MdClose size={ICON_SIZES.MD} />,
              disabled: isUpdatingSettings,
            },
          ]}
        />
      </Modal>
      {showConfirmSaveDialog && (
        <ConfirmDialog
          description={t("common_dialogs.save_changes")}
          title={t("common_dialog_titles.update")}
          onClose={() => {
            setShowConfirmSaveDialog(false);
          }}
          onConfirm={async () => {
            await updateProfileSettings();
          }}
          confirmKind={EButtonKind.SAVE}
          isLoading={isUpdatingSettings}
        />
      )}
    </>
  );
};

export default CurrentUserPrivacyModal;
